import React from 'react';
import { useForm } from 'react-hook-form';
import Input from './Input';
import Button from './Button';
import { CodePrefix } from '../../types';

interface CodePrefixFormProps {
  onSubmit: (data: Omit<CodePrefix, 'id' | 'createdAt' | 'updatedAt'>) => void;
  initialData?: CodePrefix;
  onCancel: () => void;
}

const CodePrefixForm: React.FC<CodePrefixFormProps> = ({
  onSubmit,
  initialData,
  onCancel
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: initialData || {
      prefix: '',
      description: '',
      // The NEXT number to generate: 1 means the first document gets 001
      currentSequence: 1,
      isActive: true
    }
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Prefix Code"
          {...register('prefix', {
            required: 'Prefix code is required',
            maxLength: { value: 5, message: 'Maximum 5 characters' },
            pattern: { value: /^[A-Z0-9-]+$/, message: 'Only uppercase letters, numbers, and hyphens allowed' }
          })}
          error={errors.prefix?.message}
          placeholder="E.g., E, D, M, ENG"
          maxLength={5}
        />

        <Input
          label="Description"
          {...register('description', {
            required: 'Description is required'
          })}
          error={errors.description?.message}
          placeholder="E.g., Engine Department"
        />

        <Input
          label="Next Sequence Number (1 = first document gets 001)"
          type="number"
          {...register('currentSequence', {
            required: 'Next sequence is required',
            valueAsNumber: true,
            min: { value: 1, message: 'Minimum value is 1' }
          })}
          error={errors.currentSequence?.message}
          placeholder="1"
        />

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isActive"
            {...register('isActive')}
            className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
            Active
          </label>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Prefix Code Information</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>The next generated number for this prefix will be:</p>
              <p className="font-mono mt-1">
                {initialData?.prefix || 'E'}{Math.max(1, initialData?.currentSequence || 1).toString().padStart(3, '0')}
              </p>
              <p className="mt-2">Example prefixes:</p>
              <ul className="list-disc list-inside mt-1">
                <li><strong>E</strong> - Engine Department</li>
                <li><strong>D</strong> - Deck Department</li>
                <li><strong>M</strong> - Maintenance</li>
                <li><strong>S</strong> - Supplies</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <Button variant="secondary" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          {initialData ? 'Update Prefix' : 'Add Prefix'}
        </Button>
      </div>
    </form>
  );
};

export default CodePrefixForm;

